<template>
  <div class="bg-light rounded mt-10 py-5 px-5 lg:px-10" data-testid="booking-payment-stripe-3d">
    <div class="mb-4">
      <div class="flex flex-col gap-4 md:flex-row md:justify-between items-start md:items-center">
        <h3 class="flex text-xlx justify-start items-center gap-2">
          <span
              class="h-12 w-12 bg-dark text-light rounded-full inline-flex justify-center items-center leading-none">
            <svg width="18" height="20" viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                  d="M5.875 5.625V7.5H12.125V5.625C12.125 3.90625 10.7188 2.5 9 2.5C7.24219 2.5 5.875 3.90625 5.875 5.625ZM3.375 7.5V5.625C3.375 2.53906 5.875 0 9 0C12.0859 0 14.625 2.53906 14.625 5.625V7.5H15.25C16.6172 7.5 17.75 8.63281 17.75 10V17.5C17.75 18.9062 16.6172 20 15.25 20H2.75C1.34375 20 0.25 18.9062 0.25 17.5V10C0.25 8.63281 1.34375 7.5 2.75 7.5H3.375Z"
                  fill="currentColor"/>
            </svg>
          </span>
          <span>Secure Payment</span>
        </h3>
        <img :src="`${$settings.theme_url}/assets/images/cards.png`" class="h-4 w-auto inline-block"
             alt="card logos, visa, mastercard, amex">
      </div>
    </div>
    <div class="flex flex-col gap-4">
      <div v-if="!stripeLoaded || store.totalDueCents <= 0" class="py-8 text-center text-gray-500">
        Loading payment form...
      </div>
      <VueStripeProvider :publishable-key="stripeKey" @load="onStripeLoad">
        <VueStripeElements
            v-if="stripeLoaded && store.totalDueCents > 0"
            :options="elementsOptions"
            @ready="onElementsReady"
            ref="elementsComponent"
            class="pb-4"
        >
          <VueStripePaymentElement
              @change="emit('errors', [])"
              :options="paymentElementOptions"
              ref="paymentComponent"
          />
        </VueStripeElements>
      </VueStripeProvider>

      <FieldError v-if="error !== ''" :errors="[error]"/>
    </div>
  </div>
</template>
<script setup>
import {defineProps, ref, useTemplateRef, watch} from "vue"
import {
  VueStripeProvider,
  VueStripeElements,
  VueStripePaymentElement
} from '@vue-stripe/vue-stripe'
import {storeToRefs} from "pinia";
import useStore from './../store'
import FieldError from "./FieldError.vue";
import api from "./../api";
import { useReCaptcha } from 'vue-recaptcha-v3'

const store = useStore();
// const {
//   options,
//   creditCard,
//   // intent,
//   quote,
//   form,
//   totalDueCents,
//   customer,
//   payment
// } = storeToRefs(store);

const { executeRecaptcha, recaptchaLoaded } = useReCaptcha()

const error = ref('')
const stripeInstance = ref(null)
const elementsInstance = ref(null)

const props = defineProps({
  price: {
    type: [String, Number],
    default: 0,
  },
  config: {
    type: Object,
    default: () => ({})
  }
});


/**
 * DEFERRED PAYMENT
 * This Payment Element example is based on
 * https://docs.stripe.com/payments/accept-a-payment-deferred?type=payment
 */

// const stripeKey = "pk_test_OKDoqGqz6yprOyyhtChnHXVq"
const stripeKey = props.config.token;

const elementsOptions = ref({
  // https://stripe.com/docs/js/elements_object/create#stripe_elements-options
  mode: "payment",
  amount: store.totalDueCents,
  currency: "aud",
  loader: 'always',
  // captureMethod: "manual",
  // paymentMethodCreation: 'manual',
})

const paymentElementOptions = ref({
  paymentMethodOrder: [
    'card',
    'apple_pay',
    'google_pay',
    'link'
  ],
  layout: {
    type: 'tabs',
  }
  // https://docs.stripe.com/js/elements_object/create_payment_element#payment_element_create-options
})
const stripeLoaded = ref(false)
const elementsComponent = useTemplateRef("elementsComponent")
const paymentComponent = useTemplateRef("paymentComponent")

const emit = defineEmits([
    'errors'
]);

const onStripeLoad = (stripe) => {
  stripeInstance.value = stripe
  stripeLoaded.value = true
}

const onElementsReady = (elements) => {
  elementsInstance.value = elements
  if (store.totalDueCents > 0) {
    elements.update({amount: store.totalDueCents})
  }
}

// Stripe loading is now handled by VueStripeProvider via @load event


function prepareElements(amount = store.totalDueCents) {
  elementsOptions.value = {
    ...elementsOptions.value,
    amount,
  }
  if (elementsInstance.value?.update) {
    elementsInstance.value.update({amount})
  }
}

const prepareIntent = async () => {
  return await store.preparePayment();
};

const handleError = (details) => {
  console.error(details)
  error.value = details.message;
}

watch(
    () => store.totalDueCents,
    (amount) => {
      if (amount > 0){
        prepareElements(amount)
      }
    },
    { immediate: true }
)


const recaptcha = async () => {
  // Try twice - network glitches on mobile are often momentary
  for (let attempt = 1; attempt <= 2; attempt++) {
    try {
      await recaptchaLoaded()
      const token = await executeRecaptcha('login');
      await store.setRecapture(token);
      return; // Success
    } catch (error) {
      console.warn(`reCAPTCHA attempt ${attempt}/2 failed:`, error);
      if (attempt < 2) {
        // Brief pause before retry
        await new Promise(resolve => setTimeout(resolve, 1000));
      } else {
        // Final attempt failed - continue without token
        // Server-side can handle missing reCAPTCHA
        await store.setRecapture("");
      }
    }
  }
}

const generate = async () => {
  if (!stripeInstance.value || !elementsInstance.value) {
    emit('errors', ['Please wait for the payment form to finish loading.']);
    return;
  }
  //elementsInstance.value.update({amount: store.totalDueCents});

  const result = await elementsInstance.value.submit();

  console.log('RESULT', result)

  if (result.error) {
    emit('errors', [result.error.message])
    return;
  }

  await recaptcha();
  // Confirm the PaymentIntent using the details collected by the Payment Element
  const prepared = await prepareIntent();

  if (!prepared.booking?.id){
    error.value = 'System error when creating order';
    emit('errors', [error.value])
    return;
  }

  if (stripeInstance.value) {
    try {

      // Create the ConfirmationToken using the details collected by the Payment Element
      // and additional shipping information
      const {error, confirmationToken} = await stripeInstance.value.createConfirmationToken({
        elements: elementsInstance.value,
        params: {
          payment_method_data: {
            billing_details: {
              name: `${store.customer.firstName} ${store.customer.lastName}`,
              email: store.customer.email,
              phone: store.customer.phone,
            }
          },
          return_url: store.options.confirm_url
        }
      });

      if (error) {
        // This point is only reached if there's an immediate error when
        // creating the ConfirmationToken. Show the error to your customer (for example, payment details incomplete)
        console.error(error)

        emit('errors', [error.message])
        return;
      }

      store.setPayment({
        confirmation: confirmationToken
      })

      //return true;
      console.log('confirmationToken', confirmationToken)

      return true;

      // const { error } = await stripeInstance.confirmPayment({
      //   elements,
      //   clientSecret: prepared.intent.client_secret,
      //   confirmParams: {
      //     return_url: store.options.confirm_url,
      //   },
      //   redirect: 'always' // or 'if_required'
      // })
      //
      // if (error) {
      //   // This point is only reached if there's an immediate error when
      //   // creating the ConfirmationToken. Show the error to your customer (for example, payment details incomplete)
      //   //handleError(error);
      //   console.error(error)
      //
      //   emit('errors', [error.message])
      //   return;
      // } else {
      //   // Your customer is redirected to your `return_url`. For some payment
      //   // methods like iDEAL, your customer is redirected to an intermediate
      //   // site first to authorize the payment, then redirected to the `return_url`.
      // }


    } catch (e) {
      console.error(e)
      emit('errors', [e?.message || 'Unable to process your payment details. Please try again.'])
      return false;
    }
  }

}

defineExpose({
  generate
})

</script>
